import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabase';
import Select from 'react-select';
import countryList from 'react-select-country-list';
import { State } from 'country-state-city';
import { roleCategories } from '../../constants/specialties';

const EmployerProfile = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [employerData, setEmployerData] = useState(null);
    const [jobListings, setJobListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedJobs, setSelectedJobs] = useState(new Set());
    const [hiredCount, setHiredCount] = useState(0);
    const [isEditOpen, setIsEditOpen] = useState(false);

    // Helper function to capitalize first letter of words
    const capitalizeFirstLetter = (str) => {
        if (!str) return str;
        // Handle hyphenated words like "full-time" -> "Full-time"
        return str.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join('-');
    };
    const [editForm, setEditForm] = useState({
        firstName: '',
        lastName: '',
        workEmail: '',
        companyName: '',
        companyUrl: '',
        industry: '',
        country: '',
        countryCode: '',
        province: '',
        about: '',
        socialMediaLinks: [],
    });
    const [socialMediaInputs, setSocialMediaInputs] = useState([{ platform: '', url: '' }]);
    const [logoUploading, setLogoUploading] = useState(false);
    const logoFileInputRef = useRef(null);

    // Get social media icon based on platform
    const getSocialIcon = (platform, url) => {
        switch(platform.toLowerCase()) {
            case 'linkedin':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-blue-700 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                    </a>
                );
            case 'facebook':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-blue-600 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                    </a>
                );
            case 'twitter':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-gray-800 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                        </svg>
                    </a>
                );
            case 'instagram':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-pink-600 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.155c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                    </a>
                );
            case 'youtube':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-red-600 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                    </a>
                );
            case 'website':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-gray-800 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    </a>
                );
            case 'other':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-purple-600 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                        </svg>
                    </a>
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        if (user) {
            fetchEmployerData();
            fetchJobListings();
        }
    }, [user]);

    const getPostedLabel = (dateString) => {
        if (!dateString) return "Posted date unavailable";

        const created = new Date(dateString);
        if (isNaN(created.getTime())) return "Posted date unavailable";

        const today = new Date();
        const diffMs = today - created;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays <= 0) return "Posted today";
        if (diffDays === 1) return "Posted yesterday";
        if (diffDays < 7) return `Posted ${diffDays} days ago`;
        if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            return `Posted ${weeks} week${weeks > 1 ? "s" : ""} ago`;
        }
        const months = Math.floor(diffDays / 30);
        return `Posted ${months} month${months > 1 ? "s" : ""} ago`;
    };

    const fetchEmployerData = async () => {
        try {
            setLoading(true);
            
            // Use the same method as EmployeeCompanyDetail - fetch from job listings using employer_id
            // This ensures consistency between employer profile and employee company view
            const { data: jobs, error: jobsError } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('employer_id', user.id)
                .eq('status', 'published')
                .order('created_at', { ascending: false });

            if (jobsError) {
                // Error fetching job listings
                return;
            }

            // Get company info from user metadata (same as onboarding)
            const companyInfo = user?.user_metadata?.employer_profile?.companyInfo || {};
            
            if (jobs && jobs.length > 0) {
                // Use the first job to get company info (same as EmployeeCompanyDetail)
                const firstJob = jobs[0];
                const profileData = {
                    company_name: companyInfo.companyName || firstJob.company_name || 'Your Company',
                    industry: companyInfo.industry || firstJob.industry || 'Healthcare',
                    country: firstJob.country || companyInfo.country || '',
                    countryCode: firstJob.country_code || companyInfo.countryCode || '',
                    province: firstJob.province || companyInfo.province || '',
                    contact_email: companyInfo.workEmail || firstJob.contact_email || user.email,
                    company_description: companyInfo.about || firstJob.company_description || 'We are a healthcare organization committed to providing excellent medical services.',
                    // core_values: 'Caring, Competence, Integrity, Accountability, Innovation and Responsiveness. Help us live them every day!',
                    // Prioritize user metadata company_logo_url (most up-to-date), then companyInfo.logoUrl, then job listing
                    logo_filename: user?.user_metadata?.company_logo_filename || companyInfo.logoFileName || firstJob.logo_filename,
                    logo_url: user?.user_metadata?.company_logo_url || companyInfo.logoUrl || firstJob.logo_url,
                    social_media_links: companyInfo.socialMediaLinks || firstJob.social_media_links || [],
                    firstName: companyInfo.firstName || firstJob.contact_first_name || '',
                    lastName: companyInfo.lastName || firstJob.contact_last_name || '',
                    companyUrl: companyInfo.companyUrl || firstJob.company_url || '',
                };
                setEmployerData(profileData);
                
                // Initialize social media inputs from links
                const socialLinks = profileData.social_media_links || [];
                const socialInputs = socialLinks.length > 0 
                    ? socialLinks.map(link => ({ platform: link.platform || '', url: link.url || '' }))
                    : [{ platform: '', url: '' }];
                setSocialMediaInputs(socialInputs);
                
                setEditForm({
                    firstName: profileData.firstName || '',
                    lastName: profileData.lastName || '',
                    workEmail: profileData.contact_email || '',
                    companyName: profileData.company_name || '',
                    companyUrl: profileData.companyUrl || '',
                    industry: profileData.industry || '',
                    country: profileData.country || '',
                    countryCode: profileData.countryCode || '',
                    province: profileData.province || '',
                    about: profileData.company_description || '',
                    socialMediaLinks: socialLinks,
                });
            } else {
                // Fallback to user metadata or sample data if no jobs exist yet
                const defaultProfile = {
                    company_name: companyInfo.companyName || 'Western Cape Government',
                    company_size: '1000+',
                    industry: companyInfo.industry || 'Clinical Medicine',
                    country: companyInfo.country || 'South Africa',
                    countryCode: companyInfo.countryCode || 'ZA',
                    province: companyInfo.province || 'Western Cape',
                    contact_name: 'Contact Person',
                    contact_email: companyInfo.workEmail || user.email,
                    company_description: companyInfo.about || 'As an employer, we provide ample opportunities for people who have a passion for serving others, to make a real difference in the quality of life of our citizens.',
                    // core_values: 'Caring, Competence, Integrity, Accountability, Innovation and Responsiveness. Help us live them every day!',
                    social_media_links: companyInfo.socialMediaLinks || [],
                    firstName: companyInfo.firstName || '',
                    lastName: companyInfo.lastName || '',
                    companyUrl: companyInfo.companyUrl || '',
                };
                setEmployerData(defaultProfile);
                
                // Initialize social media inputs from links
                const socialLinks = defaultProfile.social_media_links || [];
                const socialInputs = socialLinks.length > 0 
                    ? socialLinks.map(link => ({ platform: link.platform || '', url: link.url || '' }))
                    : [{ platform: '', url: '' }];
                setSocialMediaInputs(socialInputs);
                
                setEditForm({
                    firstName: defaultProfile.firstName || '',
                    lastName: defaultProfile.lastName || '',
                    workEmail: defaultProfile.contact_email || '',
                    companyName: defaultProfile.company_name || '',
                    companyUrl: defaultProfile.companyUrl || '',
                    industry: defaultProfile.industry || '',
                    country: defaultProfile.country || '',
                    countryCode: defaultProfile.countryCode || '',
                    province: defaultProfile.province || '',
                    about: defaultProfile.company_description || '',
                    socialMediaLinks: socialLinks,
                });
            }
        } catch (error) {
            // Error fetching employer data
        } finally {
            setLoading(false);
        }
    };

    const fetchJobListings = async () => {
        try {
            // Fetch job listings for this employer
            const { data: jobs, error: jobsError } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('employer_id', user.id)
                .eq('status', 'published')
                .order('created_at', { ascending: false });

            if (jobsError) {
                // Error fetching job listings
                return;
            }

            // Set the actual job listings (empty array if no jobs)
            setJobListings(jobs || []);
            
            // Fetch hired count for this employer's job listings
            const jobIds = (jobs || []).map(job => job.id);
            if (jobIds.length > 0) {
                const { count, error: countError } = await supabase
                    .from('applications')
                    .select('*', { count: 'exact', head: true })
                    .in('job_listing_id', jobIds)
                    .eq('status', 'hired');
                
                if (!countError && count !== null) {
                    setHiredCount(count);
                }
            } else {
                setHiredCount(0);
            }
        } catch (error) {
            // Error fetching job listings
        }
    };

    const handleJobSelection = (jobId) => {
        const newSelected = new Set(selectedJobs);
        if (newSelected.has(jobId)) {
            newSelected.delete(jobId);
        } else {
            newSelected.add(jobId);
        }
        setSelectedJobs(newSelected);
    };

    const handleViewJobPost = (jobId) => {
        // Use the employer job-detail route so the job loads inside EmployerLayout
        navigate(`/employer/job-detail/${jobId}`);
    };

    const getIndustryColor = (industry) => {
        const colors = {
            'Clinical Medicine': 'bg-green-100 text-green-800',
            'Healthcare': 'bg-blue-100 text-blue-800',
            'Medical Research': 'bg-purple-100 text-purple-800',
            'Pharmaceutical': 'bg-orange-100 text-orange-800',
            'default': 'bg-gray-100 text-gray-800'
        };
        return colors[industry] || colors.default;
    };

    const EditIcon = () => (
        <svg className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
    );

    const openEdit = () => {
        if (employerData) {
            // Get company info from user metadata (same as onboarding)
            const companyInfo = user?.user_metadata?.employer_profile?.companyInfo || {};
            
            const socialLinks = employerData.social_media_links || companyInfo.socialMediaLinks || [];
            const socialInputs = socialLinks.length > 0 
                ? socialLinks.map(link => ({ platform: link.platform || '', url: link.url || '' }))
                : [{ platform: '', url: '' }];
            setSocialMediaInputs(socialInputs);
            
            setEditForm({
                firstName: employerData.firstName || companyInfo.firstName || '',
                lastName: employerData.lastName || companyInfo.lastName || '',
                workEmail: employerData.contact_email || companyInfo.workEmail || '',
                companyName: employerData.company_name || companyInfo.companyName || '',
                companyUrl: employerData.companyUrl || companyInfo.companyUrl || '',
                industry: employerData.industry || companyInfo.industry || '',
                country: employerData.country || companyInfo.country || '',
                countryCode: employerData.countryCode || companyInfo.countryCode || '',
                province: employerData.province || companyInfo.province || '',
                about: employerData.company_description || companyInfo.about || '',
                socialMediaLinks: socialLinks,
            });
        }
        setIsEditOpen(true);
    };

    const handleLogoClick = () => {
        if (logoFileInputRef.current) {
            logoFileInputRef.current.click();
        }
    };

    const handleLogoUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !user) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Validate file size (e.g., max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            alert('Image size must be less than 5MB');
            return;
        }

        try {
            setLogoUploading(true);
            // Store in profile images bucket
            const bucket = process.env.REACT_APP_PROFILE_IMAGES_BUCKET || "profile-images";
            const filePath = `${user.id}/company_logo_${Date.now()}_${file.name}`;
            const { error: uploadError } = await supabase.storage.from(bucket).upload(filePath, file, {
                upsert: true,
                contentType: file.type || "image/jpeg",
            });
            if (uploadError) throw uploadError;

            // Get public URL
            const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(filePath);
            const publicUrl = publicData?.publicUrl || "";

            // Save reference in user metadata AND employer_profile.companyInfo
            const currentEmployerProfile = user?.user_metadata?.employer_profile || {};
            const currentCompanyInfo = currentEmployerProfile.companyInfo || {};
            
            const { error: metaError } = await supabase.auth.updateUser({
                data: {
                    company_logo_url: publicUrl,
                    company_logo_path: `${bucket}/${filePath}`,
                    company_logo_filename: file.name,
                    // Also update employer_profile.companyInfo so it's consistent everywhere
                    employer_profile: {
                        ...currentEmployerProfile,
                        companyInfo: {
                            ...currentCompanyInfo,
                            logoUrl: publicUrl,
                            logoFileName: file.name,
                        },
                    },
                },
            });
            if (metaError) throw metaError;

            // Update all job listings with the new logo
            const { error: updateError } = await supabase
                .from('internal_job_listings')
                .update({
                    logo_url: publicUrl,
                    logo_filename: file.name,
                })
                .eq('employer_id', user.id);
            
            if (updateError) {
                // Don't throw here, as the metadata update succeeded
            }

            // Update local state
            setEmployerData((prev) => ({
                ...prev,
                logo_url: publicUrl,
                logo_filename: file.name,
            }));

            alert("Company logo updated successfully");
        } catch (e) {
            alert(e.message || "Failed to upload company logo");
        } finally {
            setLogoUploading(false);
            // Reset file input
            if (logoFileInputRef.current) {
                logoFileInputRef.current.value = '';
            }
        }
    };

    // Social media input helper functions (matching onboarding)
    const addSocialMediaField = () => {
        setSocialMediaInputs([...socialMediaInputs, { platform: '', url: '' }]);
    };

    const removeSocialMediaField = (index) => {
        if (socialMediaInputs.length > 1) {
            setSocialMediaInputs(socialMediaInputs.filter((_, i) => i !== index));
        }
    };

    const updateSocialMediaInput = (index, field, value) => {
        const updated = [...socialMediaInputs];
        updated[index] = { ...updated[index], [field]: value };
        setSocialMediaInputs(updated);
    };

    const handleSave = async () => {
        try {
            // Sync social media links from inputs (same as onboarding)
            const validSocialMediaLinks = socialMediaInputs
                .filter(input => input.platform && input.url)
                .map(input => ({ platform: input.platform, url: input.url }));
            
            // Save company information (exactly matching onboarding step 0 save logic)
            const employerProfile = {
                ...(user?.user_metadata?.employer_profile || {}),
                companyInfo: {
                    firstName: editForm.firstName,
                    lastName: editForm.lastName,
                    workEmail: editForm.workEmail,
                    companyName: editForm.companyName,
                    companyUrl: editForm.companyUrl,
                    industry: editForm.industry,
                    about: editForm.about,
                    logoFileName: employerData?.logo_filename || user?.user_metadata?.company_logo_filename || '',
                    logoUrl: employerData?.logo_url || user?.user_metadata?.company_logo_url || '',
                    socialMediaLinks: validSocialMediaLinks,
                },
            };
            
            const { error } = await supabase.auth.updateUser({
                data: {
                    employer_profile: employerProfile,
                },
            });
            if (error) throw error;

            // Also update all employer job listings with latest company info (for backward compatibility)
            const locationString = [editForm.province, editForm.country].filter(Boolean).join(', ') || '';
            const { error: updateError } = await supabase
                .from('internal_job_listings')
                .update({
                    company_name: editForm.companyName,
                    country: editForm.country,
                    country_code: editForm.countryCode,
                    province: editForm.province,
                    location: locationString,
                    industry: editForm.industry,
                    company_description: editForm.about,
                    contact_email: editForm.workEmail,
                    contact_first_name: editForm.firstName,
                    contact_last_name: editForm.lastName,
                    company_url: editForm.companyUrl,
                    social_media_links: validSocialMediaLinks,
                })
                .eq('employer_id', user.id);
            if (updateError) {
                // Don't throw here, as the metadata update succeeded
            }

            // Refresh view
            setEmployerData((prev) => ({
                ...prev,
                company_name: editForm.companyName,
                industry: editForm.industry,
                country: editForm.country,
                countryCode: editForm.countryCode,
                province: editForm.province,
                contact_email: editForm.workEmail,
                company_description: editForm.about,
                social_media_links: validSocialMediaLinks,
                firstName: editForm.firstName,
                lastName: editForm.lastName,
                companyUrl: editForm.companyUrl,
            }));
            setIsEditOpen(false);
            alert("Company information saved successfully");
        } catch (e) {
            alert(e.message || 'Failed to save');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading profile...</p>
                </div>
            </div>
        );
    }

    if (!employerData) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600">No profile data available</p>
                </div>
            </div>
        );
    }

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .employer-profile-page {
                    padding: 1.5rem 0 !important;
                }
                .employer-profile-container {
                    padding-left: 1rem !important;
                    padding-right: 1rem !important;
                }
                .employer-profile-header {
                    margin-bottom: 1.5rem !important;
                }
                .employer-profile-header-content {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 1rem !important;
                }
                .employer-profile-header-main {
                    width: 100% !important;
                    display: flex !important;
                    align-items: center !important;
                    gap: 0.75rem !important;
                }
                .employer-profile-header-logo {
                    width: 3.5rem !important;
                    height: 3.5rem !important;
                }
                .employer-profile-header-content button {
                    align-self: stretch !important;
                    width: 100% !important;
                    justify-content: center !important;
                    border: 1px solid #e5e7eb !important;
                    border-radius: 0.75rem !important;
                    font-weight: 600 !important;
                }
                .employer-profile-card {
                    padding: 1rem !important;
                }
                .employer-profile-info-grid {
                    gap: 1rem !important;
                }
                .employer-profile-sidebar {
                    order: -1 !important;
                }
                .employer-profile-metrics-grid {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
                .employer-profile-card h2,
                .employer-profile-card h3 {
                    font-size: 1.125rem !important;
                }
                .employer-profile-about-column h1 {
                    font-size: 1.75rem !important;
                }
                .employer-profile-jobs-card {
                    padding-top: 1.5rem !important;
                }
                .employer-profile-table-wrapper {
                    overflow: visible !important;
                }
                .employer-profile-jobs-table {
                    border-collapse: separate !important;
                    border-spacing: 0 !important;
                }
                .employer-profile-jobs-table thead {
                    display: none !important;
                }
                .employer-profile-jobs-table tbody {
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                }
                .employer-profile-jobs-table tr {
                    display: flex !important;
                    flex-direction: column !important;
                    background: #ffffff !important;
                    border: 1px solid #e5e7eb !important;
                    border-radius: 0.75rem !important;
                    padding: 0.75rem !important;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.04) !important;
                }
                .employer-profile-jobs-table td {
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    width: 100% !important;
                    padding: 0.35rem 0 !important;
                    font-size: 0.9rem !important;
                }
                .employer-profile-jobs-table td::before {
                    content: attr(data-label);
                    font-weight: 600;
                    color: #4b5563;
                    text-transform: none;
                    margin-right: 0.75rem;
                }
                .employer-profile-checkbox-cell {
                    justify-content: flex-start !important;
                }
                .employer-profile-checkbox-cell::before {
                    content: 'Select' !important;
                }
                .employer-profile-job-title {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.25rem !important;
                }
                .employer-profile-job-title::before {
                    margin-bottom: 0.15rem !important;
                }
                .employer-profile-actions-cell {
                    flex-direction: column !important;
                    align-items: flex-start !important;
                    gap: 0.25rem !important;
                }
                .employer-profile-actions-cell::before {
                    margin-bottom: 0.25rem !important;
                }
                .employer-profile-view-button {
                    width: 100% !important;
                    justify-content: center !important;
                }
                .employer-profile-modal-overlay {
                    padding: 1rem !important;
                    align-items: flex-start !important;
                }
                .employer-profile-modal {
                    width: 100% !important;
                    max-width: none !important;
                    height: auto !important;
                    max-height: 90vh !important;
                    overflow-y: auto !important;
                    border-radius: 1.5rem !important;
                }
                .employer-profile-modal .grid {
                    gap: 0.75rem !important;
                }
                .employer-profile-modal .flex {
                    flex-wrap: wrap;
                }
            }
        `}</style>
        <div className="min-h-screen bg-gray-50 py-8 employer-profile-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 employer-profile-container">
                {/* Header */}
                <div className="mb-8 employer-profile-header">
                    <div className="flex items-center gap-4 employer-profile-header-content">
                        <div className="flex items-center gap-4 employer-profile-header-main">
                            <div className="relative">
                                <div 
                                    className={`w-16 h-16 flex items-center justify-center bg-gray-200 rounded-full border border-gray-200 overflow-hidden employer-profile-header-logo cursor-pointer hover:opacity-80 transition-opacity ${
                                        logoUploading ? 'opacity-50' : ''
                                    }`}
                                    onClick={handleLogoClick}
                                    title="Click to update company logo"
                                >
                                    {employerData.logo_url || user?.user_metadata?.company_logo_url ? (
                                        <img 
                                            src={employerData.logo_url || user?.user_metadata?.company_logo_url} 
                                            alt={`${employerData.company_name} logo`}
                                            className="w-full h-full object-contain"
                                        />
                                    ) : (
                                        <span className="text-lg font-medium text-gray-500">
                                            {employerData.company_name ? employerData.company_name.charAt(0).toUpperCase() : 'C'}
                                        </span>
                                    )}
                                    {logoUploading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-full">
                                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                    {!logoUploading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all rounded-full">
                                            <svg className="w-6 h-6 text-white opacity-0 hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <input
                                    ref={logoFileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoUpload}
                                    className="hidden"
                                />
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900">{employerData.company_name}</h1>
                        </div>
                        <button
                            type="button"
                            onClick={openEdit}
                            aria-label="Edit profile"
                            className="p-2 rounded hover:bg-gray-50 text-gray-600"
                        >
                            <EditIcon />
                        </button>
                    </div>
                </div>

                {/* Company Info and Sidebar */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8 employer-profile-info-grid">
                    {/* Left Column - Company Info */}
                    <div className="lg:col-span-2 employer-profile-about-column">
                        {/* About Section */}
                        <div className="bg-white rounded-xl border border-gray-200 p-4 employer-profile-card">
                            <h2 className="text-xl font-semibold text-gray-900 mb-4">About {employerData.company_name}</h2>
                            <div className="flex items-center mb-4">
                                <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-gray-600">
                                    {[employerData.province, employerData.country].filter(Boolean).join(', ') || 'Location not specified'}
                                </span>
                            </div>
                            <p className="text-gray-700 mb-4">
                                {employerData.company_description}
                            </p>
                            <div>
                                {employerData.core_values ? (
                                    <>
                                        <p className="text-gray-700 font-medium mb-2">Our core set of values are:</p>
                                        <p className="text-gray-700 font-bold">
                                            {employerData.core_values}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-gray-700 font-medium mb-2 opacity-0">Our core set of values are:</p>
                                        <p className="text-gray-700 font-bold opacity-0 h-6">
                                            {/* Placeholder to maintain spacing */}
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Company Details */}
                    <div className="space-y-6 employer-profile-sidebar">
                        {/* Connect with us - Full width */}
                        <div className="bg-white rounded-xl border border-gray-200 p-4 employer-profile-card">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Connect with us</h3>
                            {employerData.website && (
                                <div className="mb-4">
                                    <a 
                                        href={employerData.website} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline text-sm"
                                    >
                                        {employerData.website}
                                    </a>
                                </div>
                            )}
                            {employerData.social_media_links && employerData.social_media_links.length > 0 && (
                                <div className="flex space-x-4">
                                    {employerData.social_media_links.map((link, index) => (
                                        <div key={index}>
                                            {getSocialIcon(link.platform, link.url)}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Bottom row - Industry and Hired side by side */}
                        <div className="grid grid-cols-2 gap-4 employer-profile-metrics-grid">
                            {/* Industry */}
                            <div className="bg-white rounded-xl border border-gray-200 p-4 employer-profile-card">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Industry</h3>
                                <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${getIndustryColor(employerData.industry)}`}>
                                    {employerData.industry}
                                </span>
                            </div>

                            {/* Hired */}
                            <div className="bg-white rounded-xl border border-gray-200 p-4 employer-profile-card">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Hired</h3>
                                <div className="flex items-baseline">
                                    <span className="text-3xl font-bold text-gray-900">{hiredCount}</span>
                                    <span className="text-gray-600 ml-2 text-sm">positions</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Job Listings Section - Full Width */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 employer-profile-card employer-profile-jobs-card">
                    <h2 className="text-xl font-semibold text-gray-900 mb-6">
                        {jobListings.length} Open Jobs
                    </h2>
                    
                    {jobListings.length > 0 ? (
                        <div className="overflow-x-auto employer-profile-table-wrapper">
                            <table className="w-full text-sm employer-profile-jobs-table">
                                <thead>
                                    <tr className="border-b border-gray-200 employer-profile-table-header">
                                        <th className="text-left py-3 px-2 w-8">
                                            <input 
                                                type="checkbox" 
                                                className="rounded border-gray-300"
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedJobs(new Set(jobListings.map(job => job.id)));
                                                    } else {
                                                        setSelectedJobs(new Set());
                                                    }
                                                }}
                                            />
                                        </th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 uppercase">Job Title</th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 uppercase">Job Sector</th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 uppercase">Position Type</th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 uppercase">Location</th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 uppercase">Experience</th>
                                        <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 uppercase">Application</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {jobListings.map((job) => (
                                        <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50 employer-profile-job-row">
                                            <td className="py-3 px-2 employer-profile-checkbox-cell" data-label="">
                                                <input 
                                                    type="checkbox" 
                                                    className="rounded border-gray-300"
                                                    checked={selectedJobs.has(job.id)}
                                                    onChange={() => handleJobSelection(job.id)}
                                                />
                                            </td>
                                            <td className="py-3 px-2 employer-profile-job-title" data-label="Job Title">
                                                <div className="font-medium text-gray-900">{job.title}</div>
                                            </td>
                                            <td className="py-3 px-2 employer-profile-cell" data-label="Job Sector">
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getIndustryColor(job.specialty)}`}>
                                                    {job.specialty || 'Clinical Medicine'}
                                                </span>
                                            </td>
                                            <td className="py-3 px-2 employer-profile-cell" data-label="Position Type">
                                                <span className="text-sm text-gray-900">{capitalizeFirstLetter(job.employment_type || 'Full time')}</span>
                                            </td>
                                            <td className="py-3 px-2 employer-profile-cell" data-label="Location">
                                                <span className="text-sm text-gray-900">{job.location || 'Cape Town, South Africa'}</span>
                                            </td>
                                            <td className="py-3 px-2 employer-profile-cell" data-label="Experience">
                                                <span className="text-sm text-gray-900">{capitalizeFirstLetter(job.experience_level || '5 - 10 years')}</span>
                                            </td>
                                            <td className="py-3 px-2 employer-profile-actions-cell" data-label="Application">
                                                <div className="space-y-1">
                                                    <button 
                                                        onClick={() => handleViewJobPost(job.id)}
                                                        className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors employer-profile-view-button"
                                                    >
                                                        View job post
                                                    </button>
                                                    <div className="text-xs text-gray-500">
                                                        {getPostedLabel(job.created_at)}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-8">
                            <div className="text-gray-400 mb-2">
                                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                                </svg>
                            </div>
                            <p className="text-sm text-gray-500">No open jobs yet</p>
                            <p className="text-xs text-gray-400 mt-1">Create your first job posting to get started</p>
                        </div>
                    )}
                </div>
                {isEditOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 employer-profile-modal-overlay">
                        <div className="bg-white w-full max-w-2xl rounded-2xl p-6 border border-gray-200 employer-profile-modal max-h-[90vh] overflow-y-auto">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900">Edit Company Profile</h3>
                                <button onClick={() => setIsEditOpen(false)} className="text-gray-500 hover:text-gray-800">✕</button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">First Name*</label>
                                    <input className="rounded-full border border-gray-300 px-4 py-2 w-full" value={editForm.firstName} onChange={(e) => setEditForm((p) => ({ ...p, firstName: e.target.value }))} placeholder="Bob" />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Surname Name*</label>
                                    <input className="rounded-full border border-gray-300 px-4 py-2 w-full" value={editForm.lastName} onChange={(e) => setEditForm((p) => ({ ...p, lastName: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Work Email Address*</label>
                                    <input type="email" className="rounded-full border border-gray-300 px-4 py-2 w-full" value={editForm.workEmail} onChange={(e) => setEditForm((p) => ({ ...p, workEmail: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Company name*</label>
                                    <input className="rounded-full border border-gray-300 px-4 py-2 w-full" value={editForm.companyName} onChange={(e) => setEditForm((p) => ({ ...p, companyName: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Company URL*</label>
                                    <input className="rounded-full border border-gray-300 px-4 py-2 w-full" value={editForm.companyUrl} onChange={(e) => setEditForm((p) => ({ ...p, companyUrl: e.target.value }))} placeholder="https://example.com" />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Industry*</label>
                                    <select className="rounded-full border border-gray-300 px-4 py-2 w-full" value={editForm.industry} onChange={(e) => setEditForm((p) => ({ ...p, industry: e.target.value }))}>
                                        <option value="">Select</option>
                                        {roleCategories.map((category) => (
                                            <option key={category} value={category}>{category}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Country*</label>
                                    <Select
                                        options={[...countryList().getData()].sort((a, b) => (a.label === 'South Africa' ? -1 : b.label === 'South Africa' ? 1 : a.label.localeCompare(b.label)))}
                                        value={countryList().getData().find(c => c.value === editForm.countryCode || c.label === editForm.country) || null}
                                        onChange={(selectedOption) => {
                                            const countryCode = selectedOption?.value || "";
                                            setEditForm((p) => ({ 
                                                ...p, 
                                                country: selectedOption?.label || "",
                                                countryCode: countryCode,
                                                province: "" // Reset province when country changes
                                            }));
                                        }}
                                        placeholder="Select country..."
                                        isSearchable
                                        classNamePrefix="react-select"
                                        styles={{
                                            control: (provided, state) => ({
                                                ...provided,
                                                borderRadius: '9999px',
                                                borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                '&:hover': { borderColor: '#9ca3af' },
                                            }),
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Province/State*</label>
                                    {editForm.countryCode ? (() => {
                                        const states = State.getStatesOfCountry(editForm.countryCode);
                                        const stateOptions = states.map((s) => ({ value: s.isoCode, label: s.name }));
                                        const selectedState = states.find(s => s.name === editForm.province);
                                        return (
                                            <Select
                                                options={stateOptions}
                                                value={selectedState ? { value: selectedState.isoCode, label: selectedState.name } : null}
                                                onChange={(selectedOption) => {
                                                    setEditForm((p) => ({ 
                                                        ...p, 
                                                        province: selectedOption?.label || ""
                                                    }));
                                                }}
                                                placeholder="Select province/state..."
                                                isSearchable
                                                classNamePrefix="react-select"
                                                styles={{
                                                    control: (provided, state) => ({
                                                        ...provided,
                                                        borderRadius: '9999px',
                                                        borderColor: state.isFocused ? '#9ca3af' : '#d1d5db',
                                                        boxShadow: state.isFocused ? '0 0 0 1px #9ca3af' : 'none',
                                                        '&:hover': { borderColor: '#9ca3af' },
                                                    }),
                                                }}
                                            />
                                        );
                                    })() : (
                                        <input 
                                            className="w-full rounded-full border border-gray-300 px-4 py-2" 
                                            placeholder="Select country first"
                                            disabled
                                        />
                                    )}
                                </div>
                            </div>
                            
                            <div className="mt-4">
                                <label className="block text-sm text-gray-700 mb-3">Add social media links</label>
                                <div className="space-y-3">
                                    {socialMediaInputs.map((input, index) => (
                                        <div key={index} className="flex gap-2 items-center">
                                            <select 
                                                className="rounded-full border border-gray-300 px-4 py-2 flex-1"
                                                value={input.platform}
                                                onChange={(e) => updateSocialMediaInput(index, 'platform', e.target.value)}
                                            >
                                                <option value="">Select platform</option>
                                                <option value="LinkedIn">LinkedIn</option>
                                                <option value="Twitter">Twitter</option>
                                                <option value="Facebook">Facebook</option>
                                                <option value="Instagram">Instagram</option>
                                                <option value="YouTube">YouTube</option>
                                                <option value="Website">Website</option>
                                                <option value="Other">Other</option>
                                            </select>
                                            <input 
                                                type="url"
                                                className="rounded-full border border-gray-300 px-4 py-2 flex-1" 
                                                placeholder="Enter URL"
                                                value={input.url}
                                                onChange={(e) => updateSocialMediaInput(index, 'url', e.target.value)}
                                            />
                                            {(input.platform || input.url) && socialMediaInputs.length > 1 && (
                                                <button
                                                    onClick={() => removeSocialMediaField(index)}
                                                    className="text-red-600 hover:text-red-700 transition-colors"
                                                    type="button"
                                                >
                                                    <img src="/Trash 2.svg" alt="Delete" className="w-4 h-4" />
                                                </button>
                                            )}
                                            {index === socialMediaInputs.length - 1 && (
                                                <button
                                                    onClick={addSocialMediaField}
                                                    className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:bg-gray-800 transition-colors flex-shrink-0"
                                                    type="button"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-3">
                                <label className="block text-sm text-gray-700 mb-1">Tell us about your company?</label>
                                <textarea rows={4} className="w-full rounded-2xl border border-gray-200 px-4 py-3" placeholder="What additional interests do you have?" value={editForm.about} onChange={(e) => setEditForm((p) => ({ ...p, about: e.target.value }))} />
                            </div>
                            
                            <div className="mt-6 flex justify-end gap-2">
                                <button onClick={() => setIsEditOpen(false)} className="px-4 py-2 rounded-full border-2 border-gray-300">Cancel</button>
                                <button onClick={handleSave} className="px-4 py-2 rounded-full bg-black text-white">Save</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default EmployerProfile;
